import React, { useId } from 'react';
import PropTypes from 'prop-types';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import { Button, CircularProgress, useMediaQuery } from '@mui/material';

/**
 * Shared shell for every dialog in the app. Centralizes the things each modal used to
 * copy-paste or forget:
 *  - dismissal by Escape and backdrop click (no modal supported this before)
 *  - full screen on phones instead of a cramped centered card
 *  - a real <form>, so Enter submits from any field
 *  - the title wired to the dialog via aria-labelledby
 *
 * Sizing/shape defaults (maxWidth, fullWidth, radius) come from the theme, not from here.
 */
export default function AppDialog(props) {
    const {
        open,
        onClose,
        title,
        children,
        onSubmit,
        submitText = 'Save',
        cancelText = 'Cancel',
        submitColor = 'primary',
        submitDisabled = false,
        busy = false,
        hideCancel = false,
        actions,
        dividers = true,
        maxWidth,
    } = props;

    const titleId = useId();

    // matches the `width <= 768` definition of isMobile that App.js passes around, so the
    // dialog and the rest of the page agree on when we are on a phone
    const fullScreen = useMediaQuery('(max-width:768px)');

    /**
     * Swallow dismissal attempts while a request is in flight — closing mid-save leaves the
     * caller unable to report the result.
     */
    function handleClose(event, reason) {
        if (busy) {
            return;
        }
        onClose(event, reason);
    }

    function handleSubmit(event) {
        event.preventDefault();
        onSubmit(event);
    }

    const defaultActions = (
        <>
            {!hideCancel &&
                <Button variant='outlined' onClick={handleClose} disabled={busy}>
                    {cancelText}
                </Button>
            }
            {onSubmit &&
                <Button
                    type='submit'
                    variant='contained'
                    color={submitColor}
                    disabled={busy || submitDisabled}
                    startIcon={busy ? <CircularProgress size={18} color='inherit' /> : null}
                >
                    {submitText}
                </Button>
            }
        </>
    );

    const body = (
        <>
            <DialogContent dividers={dividers}>
                {children}
            </DialogContent>
            <DialogActions>
                {actions ?? defaultActions}
            </DialogActions>
        </>
    );

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            fullScreen={fullScreen}
            maxWidth={maxWidth}
            aria-labelledby={titleId}
        >
            <DialogTitle id={titleId}>{title}</DialogTitle>
            {onSubmit
                ? <form onSubmit={handleSubmit} noValidate>{body}</form>
                : body
            }
        </Dialog>
    );
}

AppDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    /** called with (event, reason) — reason is 'escapeKeyDown' | 'backdropClick' | undefined */
    onClose: PropTypes.func.isRequired,
    title: PropTypes.node.isRequired,
    children: PropTypes.node,
    /** when supplied, the body is wrapped in a form and a submit button is rendered */
    onSubmit: PropTypes.func,
    submitText: PropTypes.string,
    cancelText: PropTypes.string,
    submitColor: PropTypes.oneOf(['primary', 'secondary', 'error', 'warning', 'info', 'success']),
    submitDisabled: PropTypes.bool,
    /** request in flight: disables the buttons, shows a spinner, blocks dismissal */
    busy: PropTypes.bool,
    /** for acknowledge-only dialogs, where the single button is the only way out */
    hideCancel: PropTypes.bool,
    /** replaces the default Cancel/Submit pair entirely */
    actions: PropTypes.node,
    dividers: PropTypes.bool,
    maxWidth: PropTypes.oneOf(['xs', 'sm', 'md', 'lg', 'xl', false]),
};